import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/contexts/LanguageContext";
import { Search, X, Loader2 } from "lucide-react";

// Lazy load icon libraries only when needed
const loadIconLibrary = async (library: string) => {
  const libraries: Record<string, () => Promise<any>> = {
    fa: () => import("react-icons/fa"),
    md: () => import("react-icons/md"),
    hi: () => import("react-icons/hi"),
    bs: () => import("react-icons/bs"),
    ai: () => import("react-icons/ai"),
    fi: () => import("react-icons/fi"),
    io: () => import("react-icons/io"),
    ri: () => import("react-icons/ri"),
    bi: () => import("react-icons/bi"),
    gr: () => import("react-icons/gr"),
    go: () => import("react-icons/go"),
    si: () => import("react-icons/si"),
    im: () => import("react-icons/im"),
    di: () => import("react-icons/di"),
    cg: () => import("react-icons/cg"),
    gi: () => import("react-icons/gi"),
    pi: () => import("react-icons/pi"),
    ci: () => import("react-icons/ci"),
    lia: () => import("react-icons/lia"),
    lu: () => import("react-icons/lu"),
    rx: () => import("react-icons/rx"),
    hi2: () => import("react-icons/hi2"),
    fa6: () => import("react-icons/fa6"),
    io5: () => import("react-icons/io5"),
  };
  
  return libraries[library]?.() || null;
};

// Popular icons list (just names, not components)
const popularIconsList = [
  'fa:User', 'fa:Users', 'fa:Building', 'fa:File', 'fa:Shield', 'fa:CheckCircle',
  'fa:Envelope', 'fa:Phone', 'fa:MapMarkerAlt', 'fa:Home', 'fa:Cog', 'fa:Bars', 'fa:Search',
  'fa:ArrowRight', 'fa:ArrowLeft', 'fa:Plus', 'fa:Minus', 'fa:Edit', 'fa:Trash', 'fa:Save',
  'fa:Download', 'fa:Upload', 'fa:Eye', 'fa:EyeSlash', 'fa:Lock', 'fa:Unlock', 'fa:Star',
  'fa:Heart', 'fa:ThumbsUp', 'fa:Award', 'fa:Bullseye', 'fa:ChartLine', 'fa:ChartBar',
  'fa:Calendar', 'fa:Clock', 'fa:Bell', 'fa:Comments', 'fa:Image', 'fa:Video',
  'fa:Music', 'fa:Folder', 'fa:Database', 'fa:Server', 'fa:Cloud', 'fa:Wifi',
  'fa:Bolt', 'fa:Sun', 'fa:Moon', 'fa:Globe', 'fa:Flag', 'fa:CreditCard', 'fa:ShoppingCart',
  'fa:Box', 'fa:Truck', 'fa:Car', 'fa:Plane', 'fa:Ship', 'fa:Bicycle', 'fa:Coffee', 'fa:Utensils',
  'fa:Camera', 'fa:Microphone', 'fa:Headphones', 'fa:Gamepad', 'fa:Book', 'fa:GraduationCap',
  'fa:Briefcase', 'fa:Hammer', 'fa:Wrench', 'fa:Tools', 'fa:Filter', 'fa:Sort', 'fa:Check', 'fa:Times',
  'fa:InfoCircle', 'fa:QuestionCircle', 'fa:ExclamationTriangle', 'fa:Ban', 'fa:PowerOff', 'fa:Redo',
  'md:Person', 'md:People', 'md:Business', 'md:Description', 'md:Security', 'md:CheckCircle',
  'md:Email', 'md:Phone', 'md:LocationOn', 'md:Home', 'md:Settings', 'md:Menu', 'md:Search',
  'md:ArrowForward', 'md:ArrowBack', 'md:Add', 'md:Remove', 'md:Edit', 'md:Delete', 'md:Save',
  'md:Download', 'md:Upload', 'md:Visibility', 'md:VisibilityOff', 'md:Lock', 'md:LockOpen', 'md:Star',
  'md:Favorite', 'md:ThumbUp', 'md:EmojiEvents', 'md:Target', 'md:TrendingUp', 'md:BarChart',
  'md:CalendarToday', 'md:AccessTime', 'md:Notifications', 'md:Comment', 'md:Image', 'md:VideoLibrary',
  'md:MusicNote', 'md:Folder', 'md:Storage', 'md:Dns', 'md:Cloud', 'md:Wifi',
  'md:FlashOn', 'md:WbSunny', 'md:WbTwilight', 'md:Public', 'md:Flag', 'md:CreditCard', 'md:ShoppingCart',
] as const;

// Icon metadata (library and name only, no components)
const iconMetadata: Array<{ library: string; name: string; fullName: string }> = [
  // Font Awesome
  { library: 'fa', name: 'User', fullName: 'fa:User' },
  { library: 'fa', name: 'Users', fullName: 'fa:Users' },
  { library: 'fa', name: 'Building', fullName: 'fa:Building' },
  { library: 'fa', name: 'File', fullName: 'fa:File' },
  { library: 'fa', name: 'Shield', fullName: 'fa:Shield' },
  { library: 'fa', name: 'CheckCircle', fullName: 'fa:CheckCircle' },
  { library: 'fa', name: 'Envelope', fullName: 'fa:Envelope' },
  { library: 'fa', name: 'Phone', fullName: 'fa:Phone' },
  { library: 'fa', name: 'MapMarkerAlt', fullName: 'fa:MapMarkerAlt' },
  { library: 'fa', name: 'Home', fullName: 'fa:Home' },
  { library: 'fa', name: 'Cog', fullName: 'fa:Cog' },
  { library: 'fa', name: 'Bars', fullName: 'fa:Bars' },
  { library: 'fa', name: 'Search', fullName: 'fa:Search' },
  { library: 'fa', name: 'ArrowRight', fullName: 'fa:ArrowRight' },
  { library: 'fa', name: 'ArrowLeft', fullName: 'fa:ArrowLeft' },
  { library: 'fa', name: 'Plus', fullName: 'fa:Plus' },
  { library: 'fa', name: 'Minus', fullName: 'fa:Minus' },
  { library: 'fa', name: 'Edit', fullName: 'fa:Edit' },
  { library: 'fa', name: 'Trash', fullName: 'fa:Trash' },
  { library: 'fa', name: 'Save', fullName: 'fa:Save' },
  { library: 'fa', name: 'Download', fullName: 'fa:Download' },
  { library: 'fa', name: 'Upload', fullName: 'fa:Upload' },
  { library: 'fa', name: 'Eye', fullName: 'fa:Eye' },
  { library: 'fa', name: 'EyeSlash', fullName: 'fa:EyeSlash' },
  { library: 'fa', name: 'Lock', fullName: 'fa:Lock' },
  { library: 'fa', name: 'Unlock', fullName: 'fa:Unlock' },
  { library: 'fa', name: 'Star', fullName: 'fa:Star' },
  { library: 'fa', name: 'Heart', fullName: 'fa:Heart' },
  { library: 'fa', name: 'ThumbsUp', fullName: 'fa:ThumbsUp' },
  { library: 'fa', name: 'Award', fullName: 'fa:Award' },
  { library: 'fa', name: 'Bullseye', fullName: 'fa:Bullseye' },
  { library: 'fa', name: 'ChartLine', fullName: 'fa:ChartLine' },
  { library: 'fa', name: 'ChartBar', fullName: 'fa:ChartBar' },
  { library: 'fa', name: 'Calendar', fullName: 'fa:Calendar' },
  { library: 'fa', name: 'Clock', fullName: 'fa:Clock' },
  { library: 'fa', name: 'Bell', fullName: 'fa:Bell' },
  { library: 'fa', name: 'Comments', fullName: 'fa:Comments' },
  { library: 'fa', name: 'Image', fullName: 'fa:Image' },
  { library: 'fa', name: 'Video', fullName: 'fa:Video' },
  { library: 'fa', name: 'Music', fullName: 'fa:Music' },
  { library: 'fa', name: 'Folder', fullName: 'fa:Folder' },
  { library: 'fa', name: 'Database', fullName: 'fa:Database' },
  { library: 'fa', name: 'Server', fullName: 'fa:Server' },
  { library: 'fa', name: 'Cloud', fullName: 'fa:Cloud' },
  { library: 'fa', name: 'Wifi', fullName: 'fa:Wifi' },
  { library: 'fa', name: 'Bolt', fullName: 'fa:Bolt' },
  { library: 'fa', name: 'Sun', fullName: 'fa:Sun' },
  { library: 'fa', name: 'Moon', fullName: 'fa:Moon' },
  { library: 'fa', name: 'Globe', fullName: 'fa:Globe' },
  { library: 'fa', name: 'Flag', fullName: 'fa:Flag' },
  { library: 'fa', name: 'CreditCard', fullName: 'fa:CreditCard' },
  { library: 'fa', name: 'ShoppingCart', fullName: 'fa:ShoppingCart' },
  { library: 'fa', name: 'Box', fullName: 'fa:Box' },
  { library: 'fa', name: 'Truck', fullName: 'fa:Truck' },
  { library: 'fa', name: 'Car', fullName: 'fa:Car' },
  { library: 'fa', name: 'Plane', fullName: 'fa:Plane' },
  { library: 'fa', name: 'Ship', fullName: 'fa:Ship' },
  { library: 'fa', name: 'Bicycle', fullName: 'fa:Bicycle' },
  { library: 'fa', name: 'Coffee', fullName: 'fa:Coffee' },
  { library: 'fa', name: 'Utensils', fullName: 'fa:Utensils' },
  { library: 'fa', name: 'Camera', fullName: 'fa:Camera' },
  { library: 'fa', name: 'Microphone', fullName: 'fa:Microphone' },
  { library: 'fa', name: 'Headphones', fullName: 'fa:Headphones' },
  { library: 'fa', name: 'Gamepad', fullName: 'fa:Gamepad' },
  { library: 'fa', name: 'Book', fullName: 'fa:Book' },
  { library: 'fa', name: 'GraduationCap', fullName: 'fa:GraduationCap' },
  { library: 'fa', name: 'Briefcase', fullName: 'fa:Briefcase' },
  { library: 'fa', name: 'Hammer', fullName: 'fa:Hammer' },
  { library: 'fa', name: 'Wrench', fullName: 'fa:Wrench' },
  { library: 'fa', name: 'Tools', fullName: 'fa:Tools' },
  { library: 'fa', name: 'Filter', fullName: 'fa:Filter' },
  { library: 'fa', name: 'Sort', fullName: 'fa:Sort' },
  { library: 'fa', name: 'Check', fullName: 'fa:Check' },
  { library: 'fa', name: 'Times', fullName: 'fa:Times' },
  { library: 'fa', name: 'InfoCircle', fullName: 'fa:InfoCircle' },
  { library: 'fa', name: 'QuestionCircle', fullName: 'fa:QuestionCircle' },
  { library: 'fa', name: 'ExclamationTriangle', fullName: 'fa:ExclamationTriangle' },
  { library: 'fa', name: 'Ban', fullName: 'fa:Ban' },
  { library: 'fa', name: 'PowerOff', fullName: 'fa:PowerOff' },
  { library: 'fa', name: 'Redo', fullName: 'fa:Redo' },
  // Material Design
  { library: 'md', name: 'Person', fullName: 'md:Person' },
  { library: 'md', name: 'People', fullName: 'md:People' },
  { library: 'md', name: 'Business', fullName: 'md:Business' },
  { library: 'md', name: 'Description', fullName: 'md:Description' },
  { library: 'md', name: 'Security', fullName: 'md:Security' },
  { library: 'md', name: 'CheckCircle', fullName: 'md:CheckCircle' },
  { library: 'md', name: 'Email', fullName: 'md:Email' },
  { library: 'md', name: 'Phone', fullName: 'md:Phone' },
  { library: 'md', name: 'LocationOn', fullName: 'md:LocationOn' },
  { library: 'md', name: 'Home', fullName: 'md:Home' },
  { library: 'md', name: 'Settings', fullName: 'md:Settings' },
  { library: 'md', name: 'Menu', fullName: 'md:Menu' },
  { library: 'md', name: 'Search', fullName: 'md:Search' },
  { library: 'md', name: 'ArrowForward', fullName: 'md:ArrowForward' },
  { library: 'md', name: 'ArrowBack', fullName: 'md:ArrowBack' },
  { library: 'md', name: 'Add', fullName: 'md:Add' },
  { library: 'md', name: 'Remove', fullName: 'md:Remove' },
  { library: 'md', name: 'Edit', fullName: 'md:Edit' },
  { library: 'md', name: 'Delete', fullName: 'md:Delete' },
  { library: 'md', name: 'Save', fullName: 'md:Save' },
  { library: 'md', name: 'Download', fullName: 'md:Download' },
  { library: 'md', name: 'Upload', fullName: 'md:Upload' },
  { library: 'md', name: 'Visibility', fullName: 'md:Visibility' },
  { library: 'md', name: 'VisibilityOff', fullName: 'md:VisibilityOff' },
  { library: 'md', name: 'Lock', fullName: 'md:Lock' },
  { library: 'md', name: 'LockOpen', fullName: 'md:LockOpen' },
  { library: 'md', name: 'Star', fullName: 'md:Star' },
  { library: 'md', name: 'Favorite', fullName: 'md:Favorite' },
  { library: 'md', name: 'ThumbUp', fullName: 'md:ThumbUp' },
  { library: 'md', name: 'EmojiEvents', fullName: 'md:EmojiEvents' },
  { library: 'md', name: 'Target', fullName: 'md:Target' },
  { library: 'md', name: 'TrendingUp', fullName: 'md:TrendingUp' },
  { library: 'md', name: 'BarChart', fullName: 'md:BarChart' },
  { library: 'md', name: 'CalendarToday', fullName: 'md:CalendarToday' },
  { library: 'md', name: 'AccessTime', fullName: 'md:AccessTime' },
  { library: 'md', name: 'Notifications', fullName: 'md:Notifications' },
  { library: 'md', name: 'Comment', fullName: 'md:Comment' },
  { library: 'md', name: 'Image', fullName: 'md:Image' },
  { library: 'md', name: 'VideoLibrary', fullName: 'md:VideoLibrary' },
  { library: 'md', name: 'MusicNote', fullName: 'md:MusicNote' },
  { library: 'md', name: 'Folder', fullName: 'md:Folder' },
  { library: 'md', name: 'Storage', fullName: 'md:Storage' },
  { library: 'md', name: 'Dns', fullName: 'md:Dns' },
  { library: 'md', name: 'Cloud', fullName: 'md:Cloud' },
  { library: 'md', name: 'Wifi', fullName: 'md:Wifi' },
  { library: 'md', name: 'FlashOn', fullName: 'md:FlashOn' },
  { library: 'md', name: 'WbSunny', fullName: 'md:WbSunny' },
  { library: 'md', name: 'WbTwilight', fullName: 'md:WbTwilight' },
  { library: 'md', name: 'Public', fullName: 'md:Public' },
  { library: 'md', name: 'Flag', fullName: 'md:Flag' },
  { library: 'md', name: 'CreditCard', fullName: 'md:CreditCard' },
  { library: 'md', name: 'ShoppingCart', fullName: 'md:ShoppingCart' },
];

const ICONS_PER_PAGE = 100;

// Get prefix for library
const getLibraryPrefix = (library: string): string => {
  const prefixes: Record<string, string> = {
    fa: 'Fa',
    md: 'Md',
    hi: 'Hi',
    bs: 'Bs',
    ai: 'Ai',
    fi: 'Fi',
    io: 'Io',
    ri: 'Ri',
    bi: 'Bi',
    gr: 'Gr',
    go: 'Go',
    si: 'Si',
    im: 'Im',
    di: 'Di',
    cg: 'Cg',
    gi: 'Gi',
    pi: 'Pi',
    ci: 'Ci',
    lia: 'Lia',
    lu: 'Lu',
    rx: 'Rx',
    hi2: 'Hi',
    fa6: 'Fa',
    io5: 'Io',
  };
  return prefixes[library] || '';
};

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  placeholder?: string;
}

export const IconPicker = ({ value, onChange, placeholder }: IconPickerProps) => {
  const { t, isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadedIcons, setLoadedIcons] = useState<Record<string, any>>({});
  const [visibleCount, setVisibleCount] = useState(ICONS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Filter and sort icons based on search
  const filteredIcons = useMemo(() => {
    let filtered = iconMetadata;
    
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = iconMetadata.filter((icon) =>
        icon.name.toLowerCase().includes(searchLower) ||
        icon.library.toLowerCase().includes(searchLower) ||
        icon.fullName.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort: popular first, then alphabetically
    return [...filtered].sort((a, b) => {
      const aPopular = popularIconsList.includes(a.fullName as any);
      const bPopular = popularIconsList.includes(b.fullName as any);
      if (aPopular && !bPopular) return -1;
      if (!aPopular && bPopular) return 1;
      return a.fullName.localeCompare(b.fullName);
    });
  }, [searchQuery]);

  // Load icon component dynamically
  const loadIcon = useCallback(async (library: string, name: string) => {
    const key = `${library}:${name}`;
    if (loadedIcons[key]) return loadedIcons[key];
    
    try {
      const iconModule = await loadIconLibrary(library);
      if (iconModule) {
        // react-icons uses prefixed names (e.g., FaUser, MdPerson)
        const prefix = getLibraryPrefix(library);
        const prefixedName = `${prefix}${name}`;
        const component = iconModule[prefixedName];
        if (component && typeof component === 'function') {
          setLoadedIcons((prev) => ({ ...prev, [key]: component }));
          return component;
        }
      }
    } catch (error) {
      console.error(`Failed to load icon ${key}:`, error);
    }
    return null;
  }, [loadedIcons]);

  // Load visible icons
  useEffect(() => {
    if (!open) return;
    
    setIsLoading(true);
    const iconsToLoad = filteredIcons.slice(0, visibleCount);
    const iconsToLoadNow = iconsToLoad.filter((icon) => {
      const key = `${icon.library}:${icon.name}`;
      return !loadedIcons[key];
    });
    
    if (iconsToLoadNow.length === 0) {
      setIsLoading(false);
      return;
    }
    
    // Load icons in batches
    const batchSize = 20;
    let loadedCount = 0;
    
    const loadBatch = async (batch: typeof iconsToLoadNow) => {
      await Promise.all(
        batch.map(async (icon) => {
          await loadIcon(icon.library, icon.name);
          loadedCount++;
          if (loadedCount === iconsToLoadNow.length) {
            setIsLoading(false);
          }
        })
      );
    };
    
    for (let i = 0; i < iconsToLoadNow.length; i += batchSize) {
      const batch = iconsToLoadNow.slice(i, i + batchSize);
      setTimeout(() => loadBatch(batch), i * 50);
    }
  }, [open, filteredIcons, visibleCount, loadedIcons, loadIcon]);

  // Infinite scroll observer
  useEffect(() => {
    if (!open || visibleCount >= filteredIcons.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < filteredIcons.length) {
          setVisibleCount((prev) => Math.min(prev + ICONS_PER_PAGE, filteredIcons.length));
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [open, visibleCount, filteredIcons.length]);

  // Reset visible count when search changes
  useEffect(() => {
    setVisibleCount(ICONS_PER_PAGE);
  }, [searchQuery]);

  // Parse selected icon
  const selectedIcon = useMemo(() => {
    if (!value) return null;
    const [library, name] = value.includes(':') ? value.split(':') : ['fa', value];
    const key = `${library}:${name}`;
    return loadedIcons[key] || null;
  }, [value, loadedIcons]);

  // Load selected icon if not loaded
  useEffect(() => {
    if (value && !selectedIcon) {
      const [library, name] = value.includes(':') ? value.split(':') : ['fa', value];
      loadIcon(library, name);
    }
  }, [value, selectedIcon, loadIcon]);

  const handleSelectIcon = (icon: { library: string; name: string }) => {
    onChange(`${icon.library}:${icon.name}`);
    setOpen(false);
    setSearchQuery("");
    setVisibleCount(ICONS_PER_PAGE);
  };

  const handleClear = () => {
    onChange("");
    setSearchQuery("");
  };

  const visibleIcons = filteredIcons.slice(0, visibleCount);
  const hasMore = visibleCount < filteredIcons.length;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start"
          >
            {selectedIcon ? (
              <>
                {React.createElement(selectedIcon, { className: `w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}` })}
                <span className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>{value}</span>
              </>
            ) : (
              <span className={`flex-1 ${isRTL ? 'text-right' : 'text-left'} text-muted-foreground`}>
                {placeholder || t('common.selectIcon') || 'اختر الأيقونة'}
              </span>
            )}
            {value && (
              <X
                className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'} opacity-50 hover:opacity-100`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.searchIcons') || 'ابحث عن أيقونة...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
          </div>
          <ScrollArea className="h-[400px]">
            <div className="p-4">
              {visibleIcons.length > 0 ? (
                <>
                  <div className="grid grid-cols-10 gap-2">
                    {visibleIcons.map((icon) => {
                      const key = `${icon.library}:${icon.name}`;
                      const IconComponent = loadedIcons[key];
                      const isSelected = value === icon.fullName;
                      
                      return (
                        <button
                          key={icon.fullName}
                          type="button"
                          onClick={() => handleSelectIcon(icon)}
                          disabled={!IconComponent}
                          className={`
                            p-2 rounded-lg border transition-all
                            hover:bg-accent hover:border-primary
                            ${isSelected ? 'bg-primary text-primary-foreground border-primary' : ''}
                            ${!IconComponent ? 'opacity-50 cursor-wait' : ''}
                          `}
                          title={icon.fullName}
                        >
                          {IconComponent ? (
                            React.createElement(IconComponent, { className: "w-5 h-5", size: 20 })
                          ) : (
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {hasMore && (
                    <div ref={observerTarget} className="flex justify-center py-4">
                      {isLoading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {t('common.noIconsFound') || 'لم يتم العثور على أيقونات'}
                </div>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
      {value && (
        <div className="text-xs text-muted-foreground">
          {t('common.selectedIcon') || 'الأيقونة المختارة'}: <code className="px-1 py-0.5 bg-muted rounded">{value}</code>
        </div>
      )}
    </div>
  );
};
